import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Switch } from "../../components/ui/switch"
import { Textarea } from "../../components/ui/textarea"
import { Trash2 } from "lucide-react"

interface ProjectFormProps {
  data: {
    title: string
    description: string
    tech_stack: string
    github_url: string
    demo_url: string
    image_url: string
    featured: boolean
    image?: File | { attached?: Array<{ url: string }> } | null
  }
  setData: (field: string, value: any) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel?: () => void
  submitLabel: string
  processing: boolean
  errors: any
  onDeleteImage?: () => void
}

const ProjectForm = ({ 
  data,
  setData,
  onSubmit, 
  onCancel, 
  submitLabel, 
  processing, 
  errors,
  onDeleteImage
}: ProjectFormProps) => {
  return (
    <form onSubmit={onSubmit} className="space-y-6" name="project">
      <div className="space-y-2">
        <Label htmlFor="title">Project Title *</Label>
        <Input
          id="title"
          name="project[title]"
          value={data.title}
          onChange={(e) => setData('title', e.target.value)}
          placeholder="Enter project title"
          className={errors.title ? 'border-red-500' : ''}
          required
        />
        {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          name="project[description]"
          value={data.description}
          onChange={(e) => setData('description', e.target.value)}
          placeholder="Enter project description"
          className={errors.description ? 'border-red-500' : ''}
          rows={4}
          required
        />
        {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="tech_stack">Technologies (comma separated) *</Label>
        <Input
          id="tech_stack"
          name="project[tech_stack]"
          value={data.tech_stack}
          onChange={(e) => setData('tech_stack', e.target.value)}
          placeholder="Ruby on Rails, React, PostgreSQL"
          className={errors.tech_stack ? 'border-red-500' : ''}
          required
        />
        {errors.tech_stack && <p className="text-sm text-red-500">{errors.tech_stack}</p>}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="github_url">GitHub URL</Label>
          <Input
            id="github_url"
            name="project[github_url]"
            type="url"
            value={data.github_url}
            onChange={(e) => setData('github_url', e.target.value)}
            placeholder="https://github.com/username/project"
            className={errors.github_url ? 'border-red-500' : ''}
          />
          {errors.github_url && <p className="text-sm text-red-500">{errors.github_url}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="demo_url">Live Demo URL</Label>
          <Input
            id="demo_url"
            name="project[demo_url]"
            type="url"
            value={data.demo_url}
            onChange={(e) => setData('demo_url', e.target.value)}
            placeholder="https://project-demo.com"
            className={errors.demo_url ? 'border-red-500' : ''}
          />
          {errors.demo_url && <p className="text-sm text-red-500">{errors.demo_url}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="image_url">Image URL</Label>
        <Input
          id="image_url"
          name="project[image_url]"
          type="url"
          value={data.image_url}
          onChange={(e) => setData('image_url', e.target.value)}
          placeholder="https://example.com/image.jpg"
          className={errors.image_url ? 'border-red-500' : ''}
        />
        {errors.image_url && <p className="text-sm text-red-500">{errors.image_url}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="image">Upload Image (or use URL above)</Label>
        
        {/* Show current image if it exists */}
        {data.image && typeof data.image === 'object' && 'attached' in data.image && data.image.attached?.[0]?.url && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Current image:</p>
              {onDeleteImage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDeleteImage}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
            <img 
              src={data.image.attached[0].url} 
              alt="Current project image" 
              className="w-32 h-20 object-cover rounded border"
            />
          </div>
        )}
        
        <Input
          id="image"
          name="project[image]"
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              setData('image', file)
              // Auto-fill image_url with a placeholder that will be replaced by the server
              setData('image_url', '')
            }
          }}
          className={errors.image ? 'border-red-500' : ''}
        />
        {errors.image && <p className="text-sm text-red-500">{errors.image}</p>}
        <p className="text-xs text-muted-foreground">
          Upload a new image file or provide a URL above. If both are provided, the uploaded file will be used.
          {data.image && typeof data.image === 'object' && 'attached' in data.image && data.image.attached?.[0]?.url && " Leave empty to keep current image."}
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="featured"
          name="project[featured]"
          checked={data.featured}
          onCheckedChange={(checked) => setData('featured', checked)}
        />
        <Label htmlFor="featured">Featured</Label>
      </div>

      <div className="flex gap-3">
        <Button type="submit" disabled={processing}>
          {processing ? 'Saving...' : submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}

export default ProjectForm 